import React, { useEffect } from "react";
import { FaPhoneAlt, FaLine } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const INVESTIGATOR_IMG = "https://images.unsplash.com/photo-1498946187162-07b3c978b218?q=80&w=2070&auto=format&fit=crop";
const NETWORK_IMG = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070&auto=format&fit=crop";

const THEME = {
  fontFamily: "'Inter', 'Sarabun', sans-serif",
  pageBg: "#0f172a", // Modern Dark Slate
  textOnDark: "#f8fafc",
  textSecondary: "#94a3b8",
  accent: "#d4af37", // Signature Gold
};

const CONTACT_INFO = {
  phone: "02-276-9968",
  line: "https://line.me/R/ti/p/@443namoj",
};

export default function SpecialOpsPage() {
  const { t } = useTranslation();

  const SERVICES_LIST = [
    {
      id: "investigator",
      image: INVESTIGATOR_IMG,
      ...t("special_ops.services.investigator", { returnObjects: true })
    },
    {
      id: "network",
      image: NETWORK_IMG,
      ...t("special_ops.services.network", { returnObjects: true })
    }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ backgroundColor: THEME.pageBg, minHeight: "100vh", fontFamily: THEME.fontFamily }}>
      <header style={{
        height: "55vh", display: "flex", flexDirection: "column", justifyContent: "center",
        alignItems: "center", position: "relative", textAlign: "center", overflow: "hidden",
        backgroundColor: "#000000"
      }}>
        {/* Layer 0: Background Image */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "url('https://images.pexels.com/photos/5668473/pexels-photo-5668473.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1')",
          backgroundSize: "cover", backgroundPosition: "center", 
          opacity: 0.4, // Balanced fade
          filter: "brightness(0.7) contrast(1.1)",
          zIndex: 0
        }} />
        
        {/* Layer 1: Professional Dark Gradient (Smooth Easing) */}
        <div style={{
          position: "absolute", inset: 0,
          background: `linear-gradient(to bottom, 
            rgba(15, 23, 42, 0) 0%, 
            rgba(15, 23, 42, 0.2) 20%, 
            rgba(15, 23, 42, 0.5) 50%, 
            rgba(15, 23, 42, 0.8) 80%, 
            ${THEME.pageBg} 100%)`,
          zIndex: 1
        }} />

        <div style={{ position: "relative", zIndex: 2, padding: "0 20px" }}>
          <h1 style={{ 
            fontSize: "clamp(2.5rem, 6vw, 4.2rem)", 
            fontWeight: "900", 
            marginBottom: "15px", 
            color: THEME.accent,
            textShadow: "0 5px 30px rgba(0,0,0,0.5)"
          }}>
            {t("special_ops.hero_title")}
          </h1>
          <p style={{ 
            fontSize: "1.2rem", 
            fontWeight: "500", 
            maxWidth: "850px", 
            margin: "0 auto", 
            lineHeight: "1.8", 
            color: "#ffffff",
            textShadow: "0 2px 10px rgba(0,0,0,0.6)"
          }}>
            {t("special_ops.hero_desc")}
          </p>
        </div>
      </header>

      <main style={{ maxWidth: "1250px", margin: "0 auto", padding: "80px 20px" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
          gap: "40px",
          alignItems: "stretch"
        }}>
          {SERVICES_LIST.map((s) => (
            <div key={s.id} style={{
              backgroundColor: "#ffffff",
              borderRadius: "30px",
              overflow: "hidden",
              border: "1px solid rgba(0, 0, 0, 0.05)",
              display: "flex",
              flexDirection: "column",
              transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.04)"
            }}
              onMouseEnter={(e) => { 
                e.currentTarget.style.transform = "translateY(-12px)"; 
                e.currentTarget.style.borderColor = THEME.accent; 
                e.currentTarget.style.boxShadow = "0 20px 40px rgba(11,18,32,0.1)";
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.transform = "translateY(0)"; 
                e.currentTarget.style.borderColor = "rgba(0, 0, 0, 0.05)";
                e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.04)";
              }}
            >
              <div style={{ height: "260px", overflow: "hidden", position: "relative" }}>
                <img src={s.image} alt={s.title} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 1s ease" }} />
                <div style={{ position: "absolute", top: "20px", left: "20px", background: "rgba(11,18,32,0.8)", color: "#fff", padding: "6px 15px", borderRadius: "50px", fontSize: "11px", fontWeight: "800", letterSpacing: "1px" }}>
                  {t("nav.special_ops")}
                </div>
              </div>

              <div style={{ padding: "40px", display: "flex", flexDirection: "column", flexGrow: 1 }}>
                <div style={{ color: THEME.accent, fontWeight: "800", textTransform: "uppercase", letterSpacing: "3px", fontSize: "11px", marginBottom: "12px" }}>
                  {t("special_ops.section_kicker")}
                </div>

                <h3 style={{ fontSize: "1.8rem", fontWeight: "900", marginBottom: "10px", color: "#0b1220", lineHeight: "1.3" }}>{s.title}</h3>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "20px" }}>{s.subtitle}</div>

                <p style={{ fontSize: "1rem", lineHeight: "1.7", color: "#64748b", marginBottom: "30px" }}>{s.intro}</p>

                <div style={{ marginBottom: "35px", flexGrow: 1 }}>
                  {s.bullets && s.bullets.map((b, i) => (
                    <div key={i} style={{ marginBottom: "12px", fontSize: "0.95rem", color: "#334155", display: "flex", alignItems: "start", gap: "12px" }}>
                      <span style={{ color: THEME.accent, fontWeight: "900" }}>✓</span>
                      <span style={{ fontWeight: "500" }}>{b}</span>
                    </div>
                  ))}
                </div>

                <div style={{
                  background: "#f8fafc",
                  padding: "20px",
                  borderRadius: "20px",
                  border: "1px dashed #cbd5e1",
                }}>
                  <div style={{ fontWeight: "700", fontSize: "12px", color: "#64748b", marginBottom: "4px", textTransform: "uppercase" }}>
                    {t("special_ops.quote_label")}
                  </div>
                  <div style={{ fontSize: "24px", fontWeight: "900", color: "#0b1220" }}>{s.plan_highlight}</div>
                  <div style={{ opacity: 0.7, fontSize: "12px", marginTop: "4px", color: "#64748b" }}>{s.plan_desc}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      <section style={{ backgroundColor: "#0b1220", padding: "100px 20px", textAlign: "center" }}>
        <h2 style={{ fontSize: "3rem", fontWeight: "900", marginBottom: "30px", color: "#ffffff" }}>
          {t("special_ops.cta_title")}
        </h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", justifyContent: "center" }}>
          <div style={{ padding: "18px 45px", backgroundColor: THEME.accent, color: "#000", fontSize: "20px", fontWeight: "900", borderRadius: "50px", display: "flex", alignItems: "center", gap: "12px" }}>
            <FaPhoneAlt /> {CONTACT_INFO.phone}
          </div>
          <a href={CONTACT_INFO.line} target="_blank" rel="noopener noreferrer" style={{ padding: "18px 45px", backgroundColor: "#00c300", color: "#fff", fontSize: "20px", fontWeight: "900", borderRadius: "50px", display: "flex", alignItems: "center", gap: "12px", textDecoration: "none" }}>
            <FaLine size={24} /> {t("special_ops.cta_line")}
          </a>
        </div>
      </section>
    </div>
  );
}
